import { withAITracking } from '@microsoft/applicationinsights-react-js';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import type { Theme } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
	GridColDef,
	GridColumnVisibilityModel,
	GridRowIdGetter,
	GridSlots,
	gridStringOrNumberComparator,
} from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';

import {
	MainSegment,
	SearchProductIndividualDto,
	useProductCenterApi,
} from 'api';
import { searchProductQuery } from 'api/queries';
import { getChassis } from 'api/responses/models/Unit/UnitStructure/DrivelineSubSystem';
import { Status, statusTranslationMapping } from 'domain/constants/status';
import { formatOrderName } from 'domain/productIndividual/order';
import {
	pentaProductIndividualCategories,
	productIndividualTypeTranslation,
} from 'domain/productIndividual/types/types';
import { DataGridToolbar } from 'library/components/controls/datagrid/DataGridToolbar';
import { LocalizedDataGrid } from 'library/components/controls/datagrid/LocalizedDataGrid';
import { NoRowsOverlay } from 'library/components/controls/datagrid/NoRowsOverlay';
import {
	HighlightText,
	alphaNumericLowerCaseNormalizer,
} from 'library/components/controls/display/HighlightText';
import { PageContent } from 'library/components/controls/pageTemplates/PageContent';
import {
	SearchProductForm,
	useSearchParam,
} from 'library/components/controls/search/SearchForm/SearchProductForm';
import { EmailLink } from 'library/components/link/EmailLink';
import { Link } from 'library/components/link/Link';
import { getLocalizedDateFormat } from 'library/formatters/DateFormatter';
import { useAdobeTracking } from 'library/hooks/useAdobeTracking';
import Variables from 'library/utils/themes/Variables';
import { reactPlugin } from 'main/azureInsights/config';
import TitleWrapper from 'main/layout/TitleWrapper';
import { orderInfoPageUrl } from 'main/routes/urls/RouteUrls';
import { getFilterOperators } from 'modules/EndCustomer/Shared/DataGrid/CellRender/CountryCell';
import { ServiceButton } from 'modules/MaintenancePlanner/MaintenanceDataGrid/Components/ServiceButton';
import { PartShopLink } from 'modules/ProductIndividualview/Components/PartShopLink';
import { ProductIndividualLink } from 'modules/ProductIndividualview/Components/ProductIndividualTitleLink';
import { PublicationsLink } from 'modules/ProductIndividualview/Components/PublicationsLink';
import { RegisterProductButton } from 'modules/ProductIndividualview/Components/RegisterProductButton';
import { AssignTemporaryDrivelineButton } from 'modules/SearchProduct/AssignTemporaryDrivelineButton';
import { UnitCell } from 'modules/SearchProduct/UnitCell';
import { CampaignBadge } from 'modules/Shared/Components/ConnectedUnits/CampaignBadge';
import { unitSegmentToTranslationMapping } from 'modules/Shared/Components/ConnectedUnits/SegmentCell';
import StatusCell from 'modules/Shared/Components/ConnectedUnits/StatusCell';
import { useStoreColumnVisibilityMode } from 'modules/Shared/Components/Hooks/useStoreColumnVisibilityModel';
import { LocationCountryText } from 'modules/Shared/Components/OperatingLocation/LocationCountryText';
import { DrivelineLink } from 'modules/UnitCreation/SharedComponents/Driveline/DrivelineTitleLink';

const linkColor = {
	color: 'primary.main',
};

const SearchProductPage = ({
	initialColumnVisibilityModel,
}: {
	initialColumnVisibilityModel?: GridColumnVisibilityModel;
}): React.JSX.Element => {
	const { t } = useTranslation([
		'commonUnit',
		'commonStatus',
		'searchProduct',
		'common',
		'orderInfo',
		'serviceProtocol',
		'maintenancePlanner',
		'productIndividual',
		'productType',
	]);

	const theme = useTheme<Variables>();
	const isMobile = useMediaQuery((theme: Theme) =>
		theme.breakpoints.only('xs')
	);

	const { api } = useProductCenterApi();

	const { value: searchPhrase, change, clear } = useSearchParam();
	const { trackSiteSearch } = useAdobeTracking();

	const handleSearchChange = useCallback(
		(phrase: string) => {
			void trackSiteSearch({ type: 'searchProduct', term: phrase });
			change(phrase);
		},
		[change, trackSiteSearch]
	);

	const { enqueueSnackbar, closeSnackbar } = useSnackbar();

	const { data, isLoading, isSuccess, isError } = useQuery({
		...searchProductQuery(api, searchPhrase as string),
		staleTime: 1000,
		retry: 0,
		enabled: !!searchPhrase,
	});

	useEffect(() => {
		let notFoundSnackbar = null;
		if (isSuccess && (data == null || data.length === 0)) {
			notFoundSnackbar = enqueueSnackbar(
				t('productIndividual:pi-not-found-error'),
				{
					variant: 'error',
				}
			);
		} else if (isError) {
			enqueueSnackbar(t('common:error-unexpected'), {
				variant: 'error',
			});
		}

		return () => {
			if (notFoundSnackbar) {
				closeSnackbar(notFoundSnackbar);
			}
		};
	}, [data, isSuccess, isError, enqueueSnackbar, t, closeSnackbar]);

	const statusOptions: { value: Status; label: string }[] = useMemo(
		() =>
			Object.keys(statusTranslationMapping)
				.filter((key) => key != 'Null')
				.map((id) => ({
					value: id as Status,
					label: t(statusTranslationMapping[id as Status]),
				}))
				.sort((a, b) => a.label.localeCompare(b.label)),
		[t]
	);

	const ownerColumns = useMemo(
		() => [
			{
				field: 'ownerName',
				headerName: t('common:first-name'),
				sortable: false,
				flex: 4,
				minWidth: 150,
				rowSpanValueGetter: rowSpanValueGetter('ownerName', 'unitId'),
			},
			{
				field: 'ownerSurname',
				headerName: t('common:last-name'),
				sortable: false,
				flex: 4,
				minWidth: 150,
				rowSpanValueGetter: rowSpanValueGetter(
					'ownerSurname' as keyof SearchProductIndividualDto,
					'unitId'
				),
			},
		],
		[t]
	);

	const columns2 = useMemo(
		(): GridColDef<SearchProductIndividualDto>[] => [
			{
				field: 'unitId',
				headerName: t('commonUnit:unit-title'),
				flex: 5,
				minWidth: 320,
				sortable: false,
				filterable: false,
				renderCell: ({ value, row }) => {
					if (value) {
						return (
							<UnitCell
								id={value}
								profileImageId={row.unitImageId}
								outstandingCampaignType={
									row.unitHighestCampaignType
								}
								highlightedText={searchPhrase}
								{...row}
								publicId={row.unitPublicId!}
								linkTarget="_self"
								sx={{ p: 0.5, alignItems: 'center' }}
							/>
						);
					} else if (row.isEligibleForRegistration) {
						return (
							<RegisterProductButton
								serialNumber={row.serialNumber}
								variant="text"
							/>
						);
					}

					return null;
				},
				valueGetter: (_value, row) => row.unitId,
				rowSpanValueGetter: (value) => value,
			},
			{
				field: 'name',
				headerName: t('commonUnit:name'),
				rowSpanValueGetter: (_value, row) => row.unitId,
			},
			{
				field: 'hullId',
				headerName: t('commonUnit:hull-id'),
				rowSpanValueGetter: (_value, row) => row.unitId,
			},
			{
				field: 'hullMachineId',
				headerName: t('commonUnit:machine-id'),
				rowSpanValueGetter: (_value, row) => row.unitId,
			},
			{
				field: 'mmsiNumber',
				headerName: t('commonUnit:mmsi-number'),
				rowSpanValueGetter: (_value, row) => row.unitId,
			},
			{
				field: 'imoNumber',
				headerName: t('commonUnit:imo-number'),
				rowSpanValueGetter: (_value, row) => row.unitId,
			},
			{
				field: 'callSign',
				headerName: t('commonUnit:call-sign'),
				rowSpanValueGetter: (_value, row) => row.unitId,
			},
			{
				field: 'mainSegment',
				headerName: t('commonUnit:main-segment'),
				flex: 2,
				minWidth: 70,
				type: 'singleSelect',
				rowSpanValueGetter: rowSpanValueGetter('unitId', 'mainSegment'),
				valueOptions: () =>
					Object.keys(unitSegmentToTranslationMapping).map((id) => ({
						value: id as MainSegment,
						label: t(unitSegmentToTranslationMapping[id]),
					})),
			},
			{
				field: 'chassisId',
				headerName: t('commonUnit:chassis-id'),
				flex: 2,
				minWidth: 130,
				sortable: false,
				valueGetter: (_value, row) =>
					row.chassisSeries
						? getChassis(row.chassisSeries, row.chassisNumber)
						: null,
				renderCell: ({ value, row }) =>
					row.drivelineId ? (
						<DrivelineLink id={row.drivelineId} sx={linkColor}>
							<HighlightText
								text={value}
								phrase={searchPhrase}
								normalizer={alphaNumericLowerCaseNormalizer}
							/>
						</DrivelineLink>
					) : (
						<AssignTemporaryDrivelineButton
							serialNumber={row.serialNumber}
						/>
					),
			},
			{
				field: 'chassisActions',
				headerName: t('searchProduct:results-chassis-actions-header'),
				renderHeader: () => null,
				type: 'actions',
				display: 'text',
				width: 60,
				rowSpanValueGetter: (_value, row) => row.drivelineId,
				renderCell: ({ row }) =>
					row.drivelineId ? (
						<ServiceButton
							id={row.drivelineId}
							ButtonElement={IconButton}
							chassisId={getChassis(
								row.chassisSeries,
								row.chassisNumber
							)}
							buttonProps={{
								color: 'secondary',
								title: t(
									'serviceProtocol:generate-wizard-title'
								),
							}}
						/>
					) : null,
			},
			{
				field: 'productCategory',
				type: 'singleSelect',
				headerName: t(
					'orderInfo:product-individuals-header-product-category'
				),
				flex: 2,
				minWidth: 100,
				display: 'flex',
				valueFormatter: productIndividualTypeTranslation(t),
				valueOptions: () =>
					pentaProductIndividualCategories.map((value) => ({
						value,
						label: productIndividualTypeTranslation(t)(value),
					})),
				rowSpanValueGetter: rowSpanValueGetter(
					'productCategory',
					'serialNumber'
				),
				renderCell: ({ value, row }) => (
					<CampaignBadge campaignType={row.highestCampaignType}>
						<Box
							title={value}
							sx={(theme) => ({ p: theme.spacing(0.2, 0.5) })}>
							{row.productCategory.slice(0, 3)}
						</Box>
					</CampaignBadge>
				),
			},
			{
				field: 'serialNumber',
				headerName: t('common:serial-number'),
				flex: 2,
				minWidth: 130,
				renderCell: ({ value }) => (
					<ProductIndividualLink serialNumber={value} sx={linkColor}>
						<HighlightText text={value} phrase={searchPhrase} />
					</ProductIndividualLink>
				),
			},
			{
				field: 'productStatus',
				headerName: t('commonUnit:status'),
				type: 'singleSelect',
				flex: 1,
				align: 'center',
				minWidth: 60,
				valueOptions: statusOptions,
				renderCell: ({
					value,
					row: { serialNumber, unitPublicId },
				}) => (
					<Box display="flex" justifyContent="center" width="100%">
						<StatusCell
							status={value}
							id={unitPublicId ?? serialNumber}
						/>
					</Box>
				),
				display: 'flex',
				sortComparator: gridStringOrNumberComparator,
				valueFormatter: (value) => t(statusTranslationMapping[value]),
				rowSpanValueGetter: rowSpanValueGetter(
					'productStatus',
					'serialNumber'
				),
			},

			{
				field: 'productIndividualActions',
				headerName: t('searchProduct:results-product-actions-header'),
				renderHeader: () => null,
				type: 'actions',
				display: 'text',
				rowSpanValueGetter: (_value, row) => row.serialNumber,
				renderCell: ({ row }) => (
					<Box sx={{ '& > :first-of-type': { mr: 1 } }}>
						<PublicationsLink
							key="publicationsLink"
							serialNumber={row.serialNumber}
							isIcon
						/>
						<PartShopLink
							key="partShopLink"
							serialNumber={row.serialNumber}
							isIcon
						/>
					</Box>
				),
			},
			{
				field: 'productDesignation',
				headerName: t('common:product-designation'),
				flex: 2,
				minWidth: 140,
				rowSpanValueGetter: rowSpanValueGetter(
					'productDesignation',
					'serialNumber'
				),
			},
			{
				field: 'orderNumber',
				headerName: t('orderInfo:order-number'),
				flex: 2,
				minWidth: 130,
				valueGetter: (_value, row) =>
					formatOrderName(row.orderNumber, row.orderType),
				rowSpanValueGetter: rowSpanValueGetter(
					'orderNumber',
					'serialNumber'
				),
				renderCell: ({ value, row }) =>
					row.orderId ? (
						<Link
							to={generatePath(`/${orderInfoPageUrl}`, {
								id: row.orderId.toString(),
							})}
							sx={linkColor}>
							{value}
						</Link>
					) : null,
			},
			{
				field: 'deliveryDate',
				headerName: t('commonUnit:delivery-date'),
				type: 'date',
				flex: 2,
				minWidth: 130,
				rowSpanValueGetter: rowSpanValueGetter(
					'deliveryDate',
					'serialNumber'
				),
				valueFormatter: (value) => getLocalizedDateFormat(value),
			},
			{
				field: 'shippedToCustomerName',
				headerName: t('orderInfo:shipped-to'),
				flex: 2,
				minWidth: 150,
				rowSpanValueGetter: rowSpanValueGetter(
					'shippedToCustomerName',
					'serialNumber'
				),
			},
			{
				field: 'country',
				headerName: t('common:country'),
				type: 'singleSelect',
				flex: 2,
				minWidth: 60,
				display: 'flex',
				filterOperators: getFilterOperators,
				renderCell: ({ row }) => (
					<LocationCountryText
						countryCode={row.country}
						isDynamicWidth
						showCountryCode
					/>
				),
				rowSpanValueGetter: rowSpanValueGetter(
					'country',
					'serialNumber'
				),
			},
			{
				field: 'state',
				headerName: t('common:county'),
				flex: 2,
				minWidth: 130,
				rowSpanValueGetter: rowSpanValueGetter('state', 'serialNumber'),
			},
			{
				field: 'city',
				headerName: t('common:city'),
				flex: 2,
				minWidth: 130,
				rowSpanValueGetter: rowSpanValueGetter('city', 'serialNumber'),
			},
			{
				field: 'ownerEmail',
				headerName: t('maintenancePlanner:header-owner-email'),
				sortable: false,
				flex: 4,
				minWidth: 150,
				renderCell: (params) =>
					params.row.isOwnerAnonymous ? (
						params.row.ownerEmail
					) : (
						<EmailLink email={params.row.ownerEmail} />
					),
				rowSpanValueGetter: rowSpanValueGetter('ownerEmail', 'unitId'),
			},
			{
				field: 'ownerCompany',
				headerName: t('maintenancePlanner:header-company-name'),
				sortable: false,
				flex: 4,
				minWidth: 150,
				rowSpanValueGetter: rowSpanValueGetter(
					'ownerCompany',
					'unitId'
				),
			},
			...ownerColumns,
		],
		[t, statusOptions, ownerColumns, searchPhrase]
	);

	const getRowId: GridRowIdGetter<SearchProductIndividualDto> = useCallback(
		(params) => params.serialNumber,
		[]
	);

	const { columnVisibilityModel, updateColumnVisibilityModel } =
		useStoreColumnVisibilityMode({
			initialModel: !initialColumnVisibilityModel
				? {
						mainSegment: false,
						state: false,
						city: false,
						ownerCompany: false,
						ownerName: false,
						ownerEmail: false,
						name: false,
						hullId: false,
						hullMachineId: false,
						mmsiNumber: false,
						imoNumber: false,
						callSign: false,
					}
				: initialColumnVisibilityModel,
			category: 'SearchProductGrid',
		});

	return (
		<>
			<TitleWrapper title={t('searchProduct:search-product-header')} />
			<PageContent>
				<SearchProductForm
					sx={{
						width: { xs: 1, sm: 550 },
					}}
					defaultInput={searchPhrase ?? ''}
					onInputChange={handleSearchChange}
					onInputClear={clear}
				/>
				<Box
					id="search-product-data-grid"
					sx={{
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'flex-start',
						minHeight: 500,
						maxHeight: theme.custom.dataGridHeight,
						pt: 3,
					}}>
					<LocalizedDataGrid
						showToolbar
						slots={{
							loadingOverlay:
								LinearProgress as GridSlots['loadingOverlay'],
							toolbar: DataGridToolbar,
							noRowsOverlay: NoRowsOverlay,
						}}
						loading={isLoading}
						rows={data ?? []}
						getRowId={getRowId}
						columns={columns2}
						rowSpanning
						columnVisibilityModel={columnVisibilityModel}
						onColumnVisibilityModelChange={
							updateColumnVisibilityModel
						}
						initialState={{
							pinnedColumns: !isMobile
								? { left: ['unitId'] }
								: undefined,
						}}
					/>
				</Box>
			</PageContent>
		</>
	);
};

export default withAITracking(
	reactPlugin,
	SearchProductPage,
	'Search product page',
	'application-insight-container'
);

const rowSpanValueGetter =
	(
		propertyName: keyof SearchProductIndividualDto,
		secondPropertyName: keyof SearchProductIndividualDto
	) =>
	(value: never, row: SearchProductIndividualDto) =>
		row ? `${row[propertyName]}-${row[secondPropertyName]}` : value;
